/**
 * Session Management Utilities for TrustIQ
 * Ensures consistent session ID format across all components
 */

import { getCurrentUserInfo } from './auth';

/**
 * Generate a consistent session ID format
 * Format: {userSub}-{timestamp}-{randomId}
 * Example: "auth0|123456-20250709-194346-abc123"
 */
export async function generateSessionId() {
  try {
    const user = await getCurrentUserInfo();
    const userSub = user?.sub?.split('|').pop() || 'unknown';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const randomId = Math.random().toString(36).substring(2, 8);
    
    return `${userSub}-${timestamp}-${randomId}`;
  } catch (error) {
    console.error('Error generating session ID:', error);
    // Fallback format if auth fails
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const randomId = Math.random().toString(36).substring(2, 8);
    return `guest-${timestamp}-${randomId}`;
  }
}

/**
 * Store session ID in localStorage
 */
export function storeSessionId(sessionId) {
  localStorage.setItem('currentDocSessionId', sessionId);
  localStorage.setItem('sessionTimestamp', Date.now().toString());
}

/**
 * Retrieve stored session ID if still valid (within 24 hours)
 */
export function getStoredSessionId() {
  const storedSessionId = localStorage.getItem('currentDocSessionId');
  const sessionTimestamp = localStorage.getItem('sessionTimestamp');
  
  if (!storedSessionId || !sessionTimestamp) {
    return null;
  }
  
  // Check if session is still valid (within 24 hours)
  const isSessionValid = (Date.now() - parseInt(sessionTimestamp)) < 24 * 60 * 60 * 1000;
  
  return isSessionValid ? storedSessionId : null;
}

/**
 * Clear stored session data
 */
export function clearSessionData() {
  localStorage.removeItem('currentDocSessionId');
  localStorage.removeItem('sessionTimestamp');
  localStorage.removeItem('aiDraftingSessionId');
  localStorage.removeItem('aiDraftingSessionTimestamp');
  
  // Clear all history entries
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('aiDraftingHistory_')) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Get or create session ID
 * Returns existing valid session or creates a new one
 */
export async function getOrCreateSessionId() {
  // First check for stored session
  const storedSession = getStoredSessionId();
  if (storedSession) {
    return storedSession;
  }
  
  // Generate new session
  const newSessionId = await generateSessionId();
  storeSessionId(newSessionId);
  return newSessionId;
}

/**
 * Store session history for AI Drafting
 * @param {string} sessionId - The session ID
 * @param {Array} changes - Array of change history items
 */
export function storeSessionHistory(sessionId, changes) {
  if (!sessionId) return;

  // Do not persist attorney/client drafting history in browser storage.
  // The UI keeps the current page's history in React state only.
  localStorage.removeItem(`aiDraftingHistory_${sessionId}`);
}

/**
 * Retrieve session history for AI Drafting
 * @param {string} sessionId - The session ID
 * @returns {Array} Array of change history items
 */
export function getSessionHistory(sessionId) {
  if (!sessionId) return [];

  localStorage.removeItem(`aiDraftingHistory_${sessionId}`);
  return [];
}

/**
 * Clear session history for a specific session
 * @param {string} sessionId - The session ID
 */
export function clearSessionHistory(sessionId) {
  if (!sessionId) return;
  localStorage.removeItem(`aiDraftingHistory_${sessionId}`);
}

/**
 * Clean up old history entries to free space
 * Removes entries older than 24 hours
 */
function cleanupOldHistoryEntries() {
  const keys = Object.keys(localStorage);
  const now = Date.now();
  
  keys.forEach(key => {
    if (key.startsWith('aiDraftingHistory_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        const lastUpdated = new Date(data.lastUpdated);
        
        // Remove if older than 24 hours
        if (now - lastUpdated.getTime() > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        // If can't parse, remove it
        localStorage.removeItem(key);
      }
    }
  });
}
